import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { Loader2, ChevronDown } from "lucide-react"
import { Input } from "src/components/ui/input"
import { Textarea } from "src/components/ui/textarea"
import { Button } from "src/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "src/components/ui/dropdown-menu"
import { Switch } from "src/components/ui/switch"
import { FormItem, FormLabel, FormControl, FormMessage } from "src/components/ui/form"
import { ImageUploader } from "src/components/ImageUploader"
import { AssigneePicker } from "./AssigneePicker"
import { PROJECT_TYPES, PROJECT_STATUSES } from "../constants"

// ── Zod schema ───────────────────────────────────────────────────────────────
const projectSchema = z.object({
  name:            z.string().min(1, "Name is required").max(120, "Max 120 characters"),
  code:            z.string().max(40, "Max 40 characters").or(z.literal("")).optional(),
  project_type:    z.enum(["residential", "commercial", "agricultural", "plot"]).default("residential"),
  status:          z.enum(["new_launch", "under_construction", "ready_to_move", "sold_out"]).default("new_launch"),
  active:          z.boolean().default(true),
  assigned_membership_id: z.union([z.number(), z.string(), z.null()]).optional(),
  city:            z.string().max(80).or(z.literal("")).optional(),
  state:           z.string().max(80).or(z.literal("")).optional(),
  location:        z.string().max(255).or(z.literal("")).optional(),
  description:     z.string().max(2000).or(z.literal("")).optional(),
  launch_date:     z.string().or(z.literal("")).optional(),
  possession_date: z.string().or(z.literal("")).optional(),
})

const STATUSES = PROJECT_STATUSES
const TYPES = PROJECT_TYPES

const EMPTY = {
  name: "", code: "", project_type: "residential", status: "new_launch", active: true,
  assigned_membership_id: null, city: "", state: "",
  location: "", description: "", launch_date: "", possession_date: "",
}

export function ProjectForm({ project, onSubmit, onCancel, isPending }) {
  const isEdit = !!project
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(projectSchema),
    defaultValues: EMPTY,
  })

  useEffect(() => {
    if (project) {
      reset({
        name:            project.name ?? "",
        code:            project.code ?? "",
        project_type:    project.project_type ?? "residential",
        status:          project.status ?? "new_launch",
        active:          project.active ?? true,
        assigned_membership_id: project.assigned_to?.membership_id ?? null,
        city:            project.city ?? "",
        state:           project.state ?? "",
        location:        project.location ?? "",
        description:     project.description ?? "",
        launch_date:     project.launch_date ?? "",
        possession_date: project.possession_date ?? "",
      })
    } else {
      reset(EMPTY)
    }
  }, [project, reset])

  const status = watch("status")
  const statusLabel = STATUSES.find((s) => s.value === status)?.label ?? "New launch"
  const projectType = watch("project_type")
  const typeLabel = TYPES.find((t) => t.value === projectType)?.label ?? "Residential"
  const active = watch("active")
  const assignedId = watch("assigned_membership_id")

  // Image gallery changes live outside react-hook-form; merge them into submit.
  const [images, setImages] = useState({ newFiles: [], removedIds: [] })
  useEffect(() => { setImages({ newFiles: [], removedIds: [] }) }, [project])

  const submit = (values) => onSubmit({ ...values, __images: images })

  return (
    <form
      onSubmit={handleSubmit(submit)}
      className="flex flex-1 flex-col overflow-hidden"
    >
      <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5">
        <FormItem>
          <FormLabel>Project name</FormLabel>
          <FormControl>
            <Input placeholder="e.g. Skyline Towers" {...register("name")} />
          </FormControl>
          <FormMessage>{errors.name?.message}</FormMessage>
        </FormItem>

        <div className="grid grid-cols-2 gap-3">
          <FormItem>
            <FormLabel>Code</FormLabel>
            <FormControl>
              <Input placeholder="e.g. SKY" {...register("code")} />
            </FormControl>
            <FormMessage>{errors.code?.message}</FormMessage>
          </FormItem>

          <FormItem>
            <FormLabel>Type</FormLabel>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal text-slate-700"
                >
                  {typeLabel}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                <DropdownMenuRadioGroup
                  value={projectType}
                  onValueChange={(v) => setValue("project_type", v, { shouldDirty: true })}
                >
                  {TYPES.map((t) => (
                    <DropdownMenuRadioItem key={t.value} value={t.value}>
                      {t.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </FormItem>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormItem>
            <FormLabel>Status</FormLabel>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full justify-between font-normal text-slate-700"
                >
                  {statusLabel}
                  <ChevronDown className="h-4 w-4 opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[--radix-dropdown-menu-trigger-width]">
                <DropdownMenuRadioGroup
                  value={status}
                  onValueChange={(v) => setValue("status", v, { shouldDirty: true })}
                >
                  {STATUSES.map((s) => (
                    <DropdownMenuRadioItem key={s.value} value={s.value}>
                      {s.label}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </FormItem>

          <FormItem>
            <FormLabel>Assigned to</FormLabel>
            <div className="flex h-10 items-center rounded-md border border-slate-200 px-1.5">
              <AssigneePicker
                assignedTo={assignedId ? { membership_id: assignedId, name: project?.assigned_to?.name } : null}
                onAssign={(id) => setValue("assigned_membership_id", id, { shouldDirty: true })}
              />
            </div>
          </FormItem>
        </div>

        <FormItem className="flex items-center justify-between rounded-md border border-slate-200 px-3 py-2.5">
          <div>
            <FormLabel className="mb-0">Active</FormLabel>
            <p className="text-[12px] text-slate-400">Inactive projects are hidden from the default list.</p>
          </div>
          <Switch checked={active} onCheckedChange={(v) => setValue("active", v, { shouldDirty: true })} />
        </FormItem>

        <div className="grid grid-cols-2 gap-3">
          <FormItem>
            <FormLabel>City</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Pune" {...register("city")} />
            </FormControl>
          </FormItem>
          <FormItem>
            <FormLabel>State</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Maharashtra" {...register("state")} />
            </FormControl>
          </FormItem>
        </div>

        <FormItem>
          <FormLabel>Location</FormLabel>
          <FormControl>
            <Input placeholder="Full address / landmark" {...register("location")} />
          </FormControl>
        </FormItem>

        <div className="grid grid-cols-2 gap-3">
          <FormItem>
            <FormLabel>Launch date</FormLabel>
            <FormControl>
              <Input type="date" {...register("launch_date")} />
            </FormControl>
          </FormItem>
          <FormItem>
            <FormLabel>Possession date</FormLabel>
            <FormControl>
              <Input type="date" {...register("possession_date")} />
            </FormControl>
          </FormItem>
        </div>

        <FormItem>
          <FormLabel>Description</FormLabel>
          <FormControl>
            <Textarea rows={3} placeholder="Brief description of the project" {...register("description")} />
          </FormControl>
        </FormItem>

        <FormItem>
          <FormLabel>Images</FormLabel>
          <ImageUploader
            existing={project?.images || []}
            onChange={setImages}
            label="project images"
          />
        </FormItem>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-200 px-6 py-4">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending} className="gap-2">
          {isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          {isEdit ? "Save changes" : "Create project"}
        </Button>
      </div>
    </form>
  )
}
